import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const COMMON_LINE_HELP = {
  5: 'Enter the number of people who could be claimed as exemptions on the federal income tax return, plus any additional dependents the debtor supports. This may differ from household size.',
  6: 'Using the number of people on line 5 and the IRS National Standards, enter the allowance for food, clothing, and other items.',
  7: 'Using line 5 and the IRS National Standards, calculate the out-of-pocket health-care allowance separately for people under 65 and people age 65 or older. Any qualifying amount above this allowance may be entered on line 22.',
  '7g': 'Add the under-65 and age-65-or-older health-care subtotals from lines 7c and 7f.',
  8: 'Using the number of people on line 5, enter the U.S. Trustee Program Local Standard for housing and utilities insurance and operating expenses for the debtor’s county.',
  9: 'Use the U.S. Trustee Program housing Local Standard and the average monthly payments on mortgages and other debts secured by the home to calculate the net mortgage or rent expense.',
  '9a': 'Enter the U.S. Trustee Program Local Standard for mortgage or rent expenses for the debtor’s county and the number of people on line 5.',
  '9b': 'Add every amount contractually due in the 60 months after filing on mortgages and other debts secured by the home, then divide by 60. Repeat this amount on line 33a.',
  '9c': 'Subtract line 9b from line 9a. If the result is less than zero, enter zero.',
  10: 'Use this only if the debtor claims that the U.S. Trustee Program’s division of the housing Local Standard is incorrect and affects monthly expenses. Enter the additional amount and explain why.',
  11: 'Select the number of vehicles for which an ownership or operating expense is claimed. The official form allows 0, 1, or 2 or more vehicles.',
  12: 'Using the IRS Local Standards and the number of vehicles claimed, enter the vehicle operating costs for the applicable Census region or metropolitan statistical area.',
  13: 'Use the IRS Local Standards to calculate a net ownership or lease expense for each vehicle. No ownership expense may be claimed without a loan or lease payment, and no more than two vehicles may be claimed.',
  '13a': 'Enter the IRS Local Standard ownership or leasing cost for Vehicle 1.',
  '13b': 'For Vehicle 1, add all secured-debt amounts contractually due in the 60 months after filing and divide by 60. Do not include lease costs. Repeat this amount on line 33b.',
  '13c': 'Subtract line 13b from line 13a. If the result is less than zero, enter zero.',
  '13d': 'Enter the IRS Local Standard ownership or leasing cost for Vehicle 2.',
  '13e': 'For Vehicle 2, add all secured-debt amounts contractually due in the 60 months after filing and divide by 60. Do not include lease costs. Repeat this amount on line 33c.',
  '13f': 'Subtract line 13e from line 13d. If the result is less than zero, enter zero.',
  14: 'If zero vehicles are claimed on line 11, enter the IRS Local Standard public-transportation allowance whether or not the debtor actually uses public transportation.',
  15: 'If one or more vehicles are claimed and an additional public-transportation expense is also claimed, enter the appropriate expense, but no more than the IRS Local Standard for public transportation.',
  16: 'Enter the monthly federal, state, and local taxes actually owed, including income, self-employment, Social Security, and Medicare taxes. Subtract one-twelfth of any expected refund. Do not include real estate, sales, or use taxes.',
  17: 'Enter required monthly payroll deductions such as required retirement contributions, union dues, and uniform costs. Do not include voluntary contributions or payroll savings.',
  18: 'Enter monthly premiums for the debtor’s term life insurance and, in a joint case, the filing spouse’s term life insurance. Do not include dependent or nonfiling-spouse premiums or non-term policies.',
  19: 'Enter monthly payments required by a court or administrative agency, such as current spousal or child support. Do not include past-due support; past-due priority claims belong on line 35.',
  20: 'Enter monthly education expenses required as a condition of employment or for a physically or mentally challenged dependent child when similar public education is unavailable.',
  21: 'Enter monthly childcare expenses such as babysitting, daycare, nursery, and preschool. Do not include elementary or secondary school education.',
  22: 'Enter qualifying health-care expenses required for the debtor or dependents that are not reimbursed or paid from a health savings account, but only to the extent they exceed line 7. Insurance and HSA expenses belong on line 25.',
  23: 'Enter necessary optional telecommunication services for health, welfare, or producing income when not employer-reimbursed. Do not include basic home phone, internet, cell service, self-employment expenses, or amounts already deducted.',
  24: 'Add all expenses allowed under the IRS expense allowances on lines 6 through 23.',
  25: 'Enter reasonably necessary monthly health-insurance, disability-insurance, and health-savings-account expenses for the debtor, spouse, or dependents. If the full total is not actually spent, enter the amount actually spent.',
  '25a': 'Enter the reasonably necessary monthly health-insurance expense for the debtor, spouse, or dependents.',
  '25b': 'Enter the reasonably necessary monthly disability-insurance expense for the debtor, spouse, or dependents.',
  '25c': 'Enter the reasonably necessary monthly health-savings-account expense for the debtor, spouse, or dependents.',
  26: 'Enter actual monthly expenses that will continue for the reasonable and necessary care and support of an elderly, chronically ill, or disabled household or immediate-family member who cannot pay them. Qualified ABLE contributions may be included.',
  27: 'Enter reasonably necessary monthly expenses incurred to maintain the safety of the debtor and family under applicable family-violence laws. The court must keep the nature of these expenses confidential.',
  28: 'Enter only home-energy costs above the amount already included on line 8. The trustee must receive documentation, and the additional amount must be shown to be reasonable and necessary.',
  29: 'Enter qualifying monthly elementary or secondary school expenses for dependent children under 18, currently capped at $214.58 per child on the official form. Give the trustee documentation and explain why the amount is reasonable, necessary, and not included on lines 6–23. The cap is subject to periodic adjustment.',
  30: 'Enter the amount by which actual food and clothing expenses exceed the combined IRS National Standards allowance. The deduction cannot exceed 5% of the standard, and the additional amount must be reasonable and necessary.',
  31: 'Enter continuing cash or financial-instrument contributions to a qualifying religious or charitable organization.',
  32: 'Add all additional expense deductions on lines 25 through 31.',
  33: 'For mortgages, vehicle loans, and other debts secured by property the debtor owns, add all amounts contractually due in the 60 months after filing and divide by 60.',
  '33a': 'Copy the home-secured-debt average from line 9b.',
  '33b': 'Copy the Vehicle 1 secured-debt average from line 13b.',
  '33c': 'Copy the Vehicle 2 secured-debt average from line 13e.',
  '33d': 'List any other secured debts, identify the collateral, state whether the payment includes taxes or insurance, and enter the 60-month average payment.',
  '33e': 'Add lines 33a through 33d to obtain the total average monthly secured-debt payment.',
  34: 'For a debt on a primary residence, vehicle, or other property necessary for support, enter any additional amount needed to keep the property—the cure amount—and divide it by 60.',
  35: 'Enter past-due priority claims, such as priority taxes, child support, or alimony, as of the filing date, then divide by 60. Do not include current or ongoing amounts already entered on line 19.',
  36: 'Use the projected monthly Chapter 13 plan payment and the current district multiplier to calculate the average monthly administrative expense. On Form 122A-2, complete this only if the debtor is eligible for Chapter 13.',
  37: 'Add all deductions for debt payment on lines 33e through 36.',
  38: 'Add line 24, line 32, and line 37 to calculate total allowed deductions.',
};

const CHAPTER_7_LINE_HELP = {
  1: 'Copy total current monthly income from line 11 of Form 122A-1.',
  2: 'Use Column B of Form 122A-1 and whether the spouse is filing to determine whether line 3 may contain a marital adjustment.',
  3: 'Subtract only the part of a nonfiling spouse’s income that is not regularly used for the household expenses of the debtor or the debtor’s dependents. State each separate purpose and amount.',
  4: 'Subtract the line 3 marital adjustment from total current monthly income on line 1.',
  36: 'First indicate whether the debtor is eligible for Chapter 13. If eligible, use the projected monthly plan payment and current district multiplier to calculate the average monthly administrative expense.',
  39: 'Subtract total deductions on line 38 from adjusted current monthly income on line 4, then multiply the monthly result by 60.',
  '39c': 'Subtract line 39b total deductions from line 39a adjusted current monthly income to calculate monthly disposable income.',
  '39d': 'Multiply line 39c monthly disposable income by 60 months.',
  40: 'For cases filed on or after April 1, 2025: if line 39d is less than $10,275, there is no presumption of abuse; if it is more than $17,150, there is a presumption of abuse; if it is at least $10,275 but not more than $17,150, complete the 25% debt test on lines 41 and 42. These amounts are next subject to adjustment April 1, 2028.',
  41: 'Enter total nonpriority unsecured debt and multiply it by 25%. Form 106Sum line 3b may be used as a reference for the debt total.',
  '41a': 'Enter total nonpriority unsecured debt. Form 106Sum line 3b may be used as a reference.',
  '41b': 'Multiply line 41a by 0.25 to calculate 25% of total nonpriority unsecured debt.',
  42: 'Complete this line only when line 40 places line 39d between $10,275 and $17,150, inclusive. If line 39d is less than line 41b, check the first box for no presumption of abuse. If line 39d is equal to or more than line 41b, check the second box for a presumption of abuse; Part 4 may be completed if special circumstances are claimed.',
  43: 'Use this only for special circumstances that justify additional expenses or current-monthly-income adjustments for which there is no reasonable alternative. Give a detailed explanation and trustee documentation for each monthly amount.',
};

const CHAPTER_13_LINE_HELP = {
  31: 'Enter continuing cash or financial-instrument contributions to a qualifying religious or charitable organization. Do not include more than 15% of gross monthly income.',
  36: 'Multiply the projected monthly Chapter 13 plan payment by the current district multiplier to calculate the average monthly administrative expense.',
  39: 'Copy total current monthly income from line 14 of Form 122C-1.',
  40: 'Enter the monthly average of child support, foster-care, or disability payments received for a dependent child under applicable nonbankruptcy law, only to the extent reasonably necessary for that child’s support.',
  41: 'Enter employer-withheld contributions to qualified retirement plans plus all required repayments of loans from retirement plans.',
  42: 'Copy total allowed deductions from line 38.',
  43: 'If special circumstances justify additional expenses and there is no reasonable alternative, describe each circumstance and amount and give the trustee a detailed explanation and documentation.',
  44: 'Add lines 40 through 43 to calculate total adjustments.',
  45: 'Subtract line 44 total adjustments from line 39 current monthly income to calculate monthly disposable income under 11 U.S.C. § 1325(b)(2).',
  46: 'List income or expenses that changed, or are virtually certain to change, after filing while the case is open. Identify the source form and line, reason, date, direction, and amount of each change.',
};

const normalizeLine = line => String(line ?? '')
  .toLowerCase()
  .replace(/^line\s*/, '')
  .trim();

export function getOfficialLongFormLineHelp(chapter, line) {
  const normalized = normalizeLine(line);
  const chapterHelp = String(chapter) === '7' ? CHAPTER_7_LINE_HELP : CHAPTER_13_LINE_HELP;
  return chapterHelp[normalized]
    || COMMON_LINE_HELP[normalized]
    || COMMON_LINE_HELP[normalized.replace(/[a-z]$/, '')]
    || '';
}

export function OfficialLongFormLineHelp({ chapter, line }) {
  const helpText = getOfficialLongFormLineHelp(chapter, line);
  const tooltipId = useId();
  const buttonRef = useRef(null);
  const tooltipRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [position, setPosition] = useState(null);
  const visible = !!helpText && (hovered || focused || pinned);

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  }, []);

  const scheduleHide = useCallback(() => {
    cancelHide();
    hideTimerRef.current = window.setTimeout(() => setHovered(false), 120);
  }, [cancelHide]);

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const width = Math.min(368, Math.max(240, window.innerWidth - 32));
    const left = Math.max(16 + width / 2, Math.min(window.innerWidth - 16 - width / 2, rect.left + rect.width / 2));
    const openAbove = rect.bottom + 230 > window.innerHeight && rect.top > 230;
    setPosition(openAbove
      ? { left, bottom: window.innerHeight - rect.top + 8, width }
      : { left, top: rect.bottom + 8, width });
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition, visible]);

  useEffect(() => {
    if (!pinned) return undefined;
    const closeOnOutsidePress = event => {
      if (!buttonRef.current?.contains(event.target) && !tooltipRef.current?.contains(event.target)) {
        setPinned(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutsidePress);
    return () => document.removeEventListener('mousedown', closeOnOutsidePress);
  }, [pinned]);

  useEffect(() => () => cancelHide(), [cancelHide]);

  if (!helpText) return null;

  const togglePinned = event => {
    event.preventDefault();
    event.stopPropagation();
    updatePosition();
    setPinned(current => !current);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        title={`Official form explanation for line ${line}`}
        aria-label={`Show the official form explanation for line ${line}`}
        aria-expanded={visible}
        aria-describedby={visible ? tooltipId : undefined}
        onMouseDown={event => event.preventDefault()}
        onMouseEnter={() => { cancelHide(); setHovered(true); updatePosition(); }}
        onMouseLeave={scheduleHide}
        onFocus={() => { setFocused(true); updatePosition(); }}
        onBlur={() => setFocused(false)}
        onClick={togglePinned}
        className="ml-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-indigo-300 bg-indigo-50 text-xs font-bold leading-none text-indigo-700 hover:border-indigo-500 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
      >
        ?
      </button>
      {visible && position && createPortal(
        <div
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
          className="rounded-lg border border-indigo-200 bg-white p-3 text-left text-sm font-normal leading-5 text-gray-700 shadow-xl"
          style={{
            position: 'fixed',
            zIndex: 10000,
            left: position.left,
            top: position.top,
            bottom: position.bottom,
            width: position.width,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="mb-1 flex items-start justify-between gap-3">
            <strong className="text-indigo-900">Official Form {String(chapter) === '7' ? '122A-2' : '122C-2'}, line {line}</strong>
            {pinned && (
              <button
                type="button"
                onClick={() => setPinned(false)}
                className="-mr-1 -mt-1 rounded px-1.5 py-0.5 text-lg leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close explanation"
              >
                ×
              </button>
            )}
          </div>
          <p>{helpText}</p>
          {!pinned && <p className="mt-2 text-xs text-gray-500">Click the ? to keep this explanation open.</p>}
        </div>,
        document.body
      )}
    </>
  );
}

export function OfficialLongFormLineLabel({ chapter, line, children, className = '' }) {
  return (
    <span className={`inline-flex items-center ${className}`.trim()}>
      <span>{children}</span>
      <OfficialLongFormLineHelp chapter={chapter} line={line} />
    </span>
  );
}
